/**
 * Canal de transfert : couche applicative au-dessus du transport.
 *
 * Il assure trois choses que ni le transport ni le codec ne peuvent assurer seuls :
 *
 * 1. **La régulation de débit.** Chaque émission attend le drainage du tampon avant
 *    d'écrire la suivante. C'est ce qui borne la mémoire à quelques mégaoctets quelle
 *    que soit la taille du fichier.
 * 2. **Le traitement séquentiel.** Le déchiffrement est asynchrone et rien ne garantit
 *    que deux appels à `crypto.subtle.decrypt` se résolvent dans l'ordre d'appel. Sans
 *    sérialisation explicite, les morceaux pourraient être écrits dans le désordre et
 *    corrompre silencieusement le fichier reconstruit.
 * 3. **La détection du rejeu.** Le canal est fiable et ordonné : la séquence doit donc
 *    être strictement croissante. Tout écart signale une anomalie réelle — rejeu,
 *    duplication ou défaut de programmation — et interrompt le transfert plutôt que de
 *    produire un fichier silencieusement faux.
 */

import type { Bytes, DcControl } from '@attrape/shared';
import { FrameCounter } from '../crypto.js';
import type { FrameCodec } from '../framing.js';
import type { Transport } from '../transport.js';
import { LocalizedError, type MessageDescriptor } from '../../i18n/index.js';

export interface TransferChannelHandlers {
  /**
   * Les gestionnaires principaux sont **attendus** : une écriture disque asynchrone doit
   * être terminée avant que le message suivant ne soit traité. Sans cela, un `file-done`
   * pourrait être traité alors que les derniers morceaux sont encore en cours d'écriture,
   * et l'empreinte serait comparée sur un fichier incomplet.
   */
  onControl?: (control: DcControl) => void | Promise<void>;
  onChunk?: (fileIndex: number, data: Bytes) => void | Promise<void>;
  /** Appelé sur anomalie protocolaire : le transfert doit être interrompu. */
  onProtocolError: (message: string) => void;
}

export class TransferChannel {
  private readonly counter = new FrameCounter();
  private expectedSeq = 0;
  private closed = false;
  /** Chaîne de sérialisation : garantit le traitement dans l'ordre d'arrivée. */
  private queue: Promise<void> = Promise.resolve();

  private readonly controlHandlers = new Set<(control: DcControl) => void>();
  private readonly chunkHandlers = new Set<(fileIndex: number, data: Bytes) => void>();
  private readonly unsubscribe: () => void;

  constructor(
    private readonly transport: Transport,
    private readonly codec: FrameCodec,
    private readonly handlers: TransferChannelHandlers,
  ) {
    this.unsubscribe = transport.onMessage((raw) => {
      this.queue = this.queue
        .then(() => this.process(raw))
        .catch((error: unknown) => {
          this.handlers.onProtocolError(describeError(error));
        });
    });
  }

  private async process(raw: Bytes): Promise<void> {
    if (this.closed) return;

    const { seq, decoded } = await this.codec.decode(raw);

    if (seq !== this.expectedSeq) {
      throw new Error(
        `Séquence de trame inattendue : reçue ${seq}, attendue ${this.expectedSeq}. ` +
          'Le flux a été altéré ou rejoué.',
      );
    }
    this.expectedSeq += 1;

    if (decoded.kind === 'control') {
      await this.handlers.onControl?.(decoded.control);
      for (const handler of this.controlHandlers) handler(decoded.control);
      return;
    }

    await this.handlers.onChunk?.(decoded.fileIndex, decoded.data);
    for (const handler of this.chunkHandlers) handler(decoded.fileIndex, decoded.data);
  }

  /** S'abonne aux messages de contrôle. Renvoie une fonction de désabonnement. */
  onControl(handler: (control: DcControl) => void): () => void {
    this.controlHandlers.add(handler);
    return () => this.controlHandlers.delete(handler);
  }

  /**
   * S'abonne à la fermeture du transport sous-jacent.
   *
   * Indispensable en réception : sans cela, un émetteur qui disparaît sans envoyer ses
   * contrôles de fin laisserait le récepteur en attente **indéfiniment**. C'est le
   * « transfert qui reste bloqué » que le `PLAN.md` §1.3 reproche précisément aux
   * services existants, et il doit être signalé, pas subi.
   */
  onClose(handler: () => void): () => void {
    return this.transport.onClose(handler);
  }

  /** S'abonne aux morceaux de données. Renvoie une fonction de désabonnement. */
  onChunk(handler: (fileIndex: number, data: Bytes) => void): () => void {
    this.chunkHandlers.add(handler);
    return () => this.chunkHandlers.delete(handler);
  }

  /**
   * Émet un message de contrôle, après drainage du tampon.
   *
   * Les messages de contrôle passent par la même file que les données : ils ne peuvent
   * donc pas dépasser un morceau de fichier encore en vol. C'est ce qui garantit qu'un
   * `file-done` arrive bien après le dernier morceau du fichier concerné.
   */
  async sendControl(control: DcControl, signal?: AbortSignal): Promise<void> {
    await this.transport.waitForDrain(signal);
    const frame = await this.codec.encodeControl(control, this.counter.take());
    this.transport.send(frame);
  }

  /** Émet un morceau de fichier, après drainage du tampon. */
  async sendChunk(fileIndex: number, data: Bytes, signal?: AbortSignal): Promise<void> {
    await this.transport.waitForDrain(signal);
    const frame = await this.codec.encodeChunk(fileIndex, this.counter.take(), data);
    this.transport.send(frame);
  }

  get bytesBuffered(): number {
    return this.transport.bufferedAmount;
  }

  get isOpen(): boolean {
    return !this.closed && this.transport.isOpen;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.unsubscribe();
    this.controlHandlers.clear();
    this.chunkHandlers.clear();
  }
}

/** Message d'erreur exploitable, quelle que soit la forme de l'exception. */
export function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Erreur inconnue';
}

export interface WaitForControlOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Motif du dépassement de délai, sous forme traduisible. */
  timeoutMessage?: MessageDescriptor;
}

/**
 * Attend un message de contrôle satisfaisant le prédicat.
 *
 * S'utilise pour les points de rendez-vous du protocole (attente de `accept`, de
 * `all-done`…). Le désabonnement systématique évite qu'un transfert abandonné laisse
 * des écouteurs derrière lui.
 */
export function waitForControl(
  channel: TransferChannel,
  predicate: (control: DcControl) => boolean,
  options: WaitForControlOptions = {},
): Promise<DcControl> {
  const { signal, timeoutMs, timeoutMessage } = options;

  return new Promise<DcControl>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const done = (): void => {
      unsubscribe();
      if (timer !== null) clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = (): void => {
      done();
      reject(new DOMException('Transfert annulé', 'AbortError'));
    };

    const unsubscribe = channel.onControl((control) => {
      if (!predicate(control)) return;
      done();
      resolve(control);
    });

    if (TimeoutIsFinite(timeoutMs)) {
      timer = setTimeout(() => {
        done();
        reject(
          timeoutMessage === undefined
            ? new Error('Délai dépassé en attendant une réponse du pair.')
            : new LocalizedError(timeoutMessage),
        );
      }, timeoutMs);
    }

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

/** `Number.isFinite` avec le type attendu, pour éviter une comparaison implicite. */
function TimeoutIsFinite(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
